import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { can } from "../auth/rbac";
import { OsLogo } from "../components/domain/OsLogo";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { useToast } from "../components/ui/Toast";

/**
 * Panelden erişilen agent kurulum rehberi. Komutlar sayfanın açıldığı
 * origin'i kullanır; böylece test/lab ve üretim adresleri ayrı ayrı
 * güncellenmek zorunda kalmaz.
 */
const baseUrl =
  typeof window !== "undefined" && window.location.origin
    ? window.location.origin
    : "https://netmonitor.bilir.ai";

export function InstallPage() {
  const { user } = useAuth();
  const [token, setToken] = useState("");
  const canManageEnrollment = can(user?.role, "manageEnrollment");

  const cleanToken = token.trim();
  const tokenArg = cleanToken || "PANELDEN_ALINAN_TOKEN";
  const linuxTokenCommand = `curl -fsSL ${baseUrl}/install | bash -s -- --token "${tokenArg}"`;
  const windowsTokenCommand = `& ([scriptblock]::Create((irm ${baseUrl}/install.ps1))) -Token "${tokenArg}"`;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Agent Kurulumu</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Windows ve Linux uç noktalarını tek komutla kurup panele kaydedin.
          </p>
        </div>
        {canManageEnrollment ? (
          <Link to="/settings/enrollment">
            <Button variant="outline" size="sm">
              Enrollment token üret
            </Button>
          </Link>
        ) : null}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>1. Enrollment token</CardTitle>
          <CardDescription>
            Token tek kullanımlıktır ve varsayılan olarak 24 saat geçerlidir. Her cihaz için
            ayrı token üretin. Token'ı aşağıya yazarsanız komutlar hazır hale gelir; sayfa
            yenilenince token tarayıcıdan silinir.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Input
            label="Enrollment token (opsiyonel)"
            placeholder="PANELDEN_ALINAN_TOKEN"
            hint="Boş bırakırsanız kurulum token'ı gizli olarak sorar."
            value={token}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => setToken(event.target.value)}
          />
          {!canManageEnrollment ? (
            <p className="text-muted-foreground mt-3 text-xs">
              Token üretme yetkiniz yok; kurulum için yöneticinizden token isteyin.
            </p>
          ) : null}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2">
                <OsLogo osFamily="linux" className="h-5 w-5" />
                Linux
              </CardTitle>
              <Badge tone="info">systemd</Badge>
            </div>
            <CardDescription>
              Debian 11+, Ubuntu 20.04+, RHEL 8+, Rocky ve AlmaLinux. Root yetkisi ve
              sunucuya 443/tcp erişimi gerekir.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <CommandBlock
              label="Token gizli sorulsun (önerilen)"
              command={`curl -fsSL ${baseUrl}/install | bash`}
            />
            <CommandBlock label="Token hazırsa" command={linuxTokenCommand} />
            <div className="text-muted-foreground text-xs">
              Kurulum sonrası kontrol:{" "}
              <code className="font-mono">systemctl status nagios-agent --no-pager</code>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2">
                <OsLogo osFamily="windows" className="h-5 w-5" />
                Windows
              </CardTitle>
              <Badge tone="info">PowerShell</Badge>
            </div>
            <CardDescription>
              Windows 10 / Server 2016+ (x64, arm64). PowerShell yönetici değilse UAC ile
              kendini yükseltir; sunucuya 443/tcp erişimi gerekir.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <CommandBlock
              label="Token gizli sorulsun (önerilen)"
              command={`irm ${baseUrl}/install.ps1 | iex`}
            />
            <CommandBlock label="Token hazırsa" command={windowsTokenCommand} />
            <CommandBlock
              label="CMD üzerinden (token gizli sorulur)"
              command={`powershell -NoProfile -ExecutionPolicy Bypass -Command "irm ${baseUrl}/install.ps1 | iex"`}
            />
            <div className="text-muted-foreground text-xs">
              Kurulum sonrası kontrol:{" "}
              <code className="font-mono">Get-Service NagiosAgent</code>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2">
              <OsLogo osFamily="linux" className="h-5 w-5" />
              Home Assistant OS (HAOS)
            </CardTitle>
            <Badge tone="warning">Add-on</Badge>
          </div>
          <CardDescription>
            HAOS normal Linux paketlerini ve systemd'yi çalıştırmaz. Raspberry Pi gibi HAOS
            cihazlarında agent'ı Home Assistant add-on olarak kurun.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="text-muted-foreground list-decimal space-y-1.5 pl-5 text-sm">
            <li>Panelden bir enrollment token üretin.</li>
            <li>
              Depodaki <code className="font-mono">deploy/homeassistant/netmonitor_agent</code>{" "}
              klasörünü HAOS'un <code className="font-mono">/addons</code> dizinine kopyalayın
              (Samba: <code className="font-mono">\\HAOS-IP\addons\netmonitor_agent</code>).
            </li>
            <li>
              <strong>Ayarlar → Eklentiler → Eklenti Mağazası</strong> menüsünde sağ üstteki
              üç noktadan <strong>Güncellemeleri kontrol et</strong> deyin; eklenti{" "}
              <strong>Yerel eklentiler</strong> altında görünür.
            </li>
            <li>
              <strong>Netmonitor Agent</strong> eklentisini kurun; yapılandırmada{" "}
              <code className="font-mono">server_url</code>, token, site ve etiketleri girin.
              Bu ortamda <code className="font-mono">mtls: false</code> kullanın.
            </li>
            <li>
              Eklentiyi başlatın ve günlüğü kontrol edin. İlk kayıttan sonra token alanını
              temizleyin.
            </li>
          </ol>
          <div className="mt-4">
            <CommandBlock
              label="HAOS güncellemesi (terminalde)"
              command={`curl -fsSL ${baseUrl}/download/netmonitor-agent-addon.tar.gz | tar -xz -C /addons`}
            />
            <p className="text-muted-foreground mt-2 text-xs">
              Ardından Eklenti Mağazası'nda <strong>Güncellemeleri kontrol et</strong> deyip
              Netmonitor Agent'ı güncelleyin veya yeniden derleyin.
            </p>
          </div>
          <p className="text-muted-foreground mt-3 text-xs">
            Not: eklenti host ağı ve host adını kullanır; CPU/RAM ve ağ görünürlüğü çalışır.
            HAOS host kökü, disk envanteri, HAOS güncellemeleri ve Home Assistant varlıkları bu
            agent kapsamında değildir. Ayrıntılar eklenti README dosyasındadır.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Kurulum notları</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="text-muted-foreground list-disc space-y-1.5 pl-5 text-sm">
            <li>Her kurulum için panelden yeni bir enrollment token alın; token tek kullanımlıktır.</li>
            <li>
              Yeniden kurulumda token verilmezse mevcut agent kimliği korunur. Token verilirse
              eski state yedeklenir ve cihaz yeniden kaydedilir.
            </li>
            <li>
              Bu ortamda TLS reverse proxy'de sonlanır; kurulum varsayılan olarak mTLS kapalı
              çalışır. Uçtan uca istemci sertifikası için <code className="font-mono">-Mtls on</code>{" "}
              kullanın.
            </li>
            <li>
              Windows'ta <code className="font-mono">-Site</code>,{" "}
              <code className="font-mono">-Tags</code>, <code className="font-mono">-DryRun</code>;{" "}
              Linux'ta <code className="font-mono">--site</code> ve{" "}
              <code className="font-mono">--tags</code> gibi ek parametreler kullanılabilir.
            </li>
            <li>
              Ayrıntılı parametreler ve sorun giderme için depodaki{" "}
              <code className="font-mono">docs/AGENT-INSTALL.md</code> dosyasına bakın.
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

function CommandBlock({ label, command }: { label: string; command: string }) {
  const { toast } = useToast();

  async function copyCommand() {
    try {
      await navigator.clipboard.writeText(command);
      toast("Komut panoya kopyalandı.", "success");
    } catch {
      toast("Kopyalama başarısız; komutu elle seçip kopyalayın.", "error");
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-muted-foreground text-xs font-medium">{label}</p>
        <Button variant="ghost" size="sm" onClick={() => void copyCommand()}>
          Kopyala
        </Button>
      </div>
      <pre className="bg-muted text-foreground overflow-x-auto rounded-md border border-border p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap break-all">
        <code>{command}</code>
      </pre>
    </div>
  );
}
